import { useEffect, useRef, useState } from 'react'
import { Sheet, Typography, Box, Link, Button, CircularProgress, Chip } from '@mui/joy'
import QRCodeStyling from 'qr-code-styling'
import { useTheme } from '@mui/joy/styles'
import { CaretDownIcon } from '@phosphor-icons/react'
import { AnimatePresence, motion } from 'framer-motion'
import FormatDateFull, { FormatDate } from '../FormatDateFull/FormatDateFull'

export default function AppCard({
    title,
    link,
    linkQr,
    moreLink = {},
    icon,
    target = '_blank',
    loading,
    error,
    data
}) {
    const theme = useTheme()
    const qrRef = useRef(null)
    const [isInfoOpen, setIsInfoOpen] = useState(false)

    useEffect(() => {
        if (!qrRef.current) return
        const logoBgColor = theme.colors?.logo || '#0ABAB5'

        const qr = new QRCodeStyling({
            width: 240,
            height: 240,
            type: 'svg',
            data: linkQr,
            qrOptions: {
                errorCorrectionLevel: 'M'
            },
            dotsOptions: {
                color: logoBgColor,
                type: 'rounded',
            },
            cornersSquareOptions: {
                type: 'extra-rounded',
                color: logoBgColor,
            },
            backgroundOptions: {
                color: 'transparent',
            },
            imageOptions: {
                margin: 4,
                crossOrigin: 'anonymous',
            },
        })

        qrRef.current.innerHTML = ''

        setTimeout(() => {
            if (qrRef.current) {
                qr.append(qrRef.current)
            }
        }, 0)

    }, [linkQr, theme])

    return (
        <Sheet
            variant="soft"
            sx={{
                p: 2,
                borderRadius: '24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flex: 1,
                height: '100%',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    width: '100%',
                    gap: 2,
                    alignItems: 'center',
                    flexWrap: 'wrap'
                }}
            >
                <Box
                    ref={qrRef}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: 1,
                        width: 160,
                        height: 160,
                        maxWidth: 160,
                        '& svg': {
                            width: 160,
                            height: 160,
                            maxWidth: 160
                        },
                    }}
                />

                <Box>
                    <Typography
                        level="h3"
                        startDecorator={icon}
                        textAlign="center"
                        sx={{ my: 2 }}
                    >
                        {title}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            flexDirection: 'row',
                            gap: 2,
                            alignItems: 'center'
                        }}
                    >
                        <Button
                            component="a"
                            href={link}
                            target={target}
                            level="body-sm"
                            size="sm"
                            sx={{
                                textDecoration: 'none !important',
                                borderRadius: '99px',
                                px: 2,
                                mb: 1.5
                            }}
                        >
                            Скачать
                        </Button>

                        {!!moreLink.title && (
                            <Link
                                href={moreLink.href}
                                target="_blank"
                                level="body-sm"
                                startDecorator={moreLink.icon}
                                sx={{ mb: 1 }}
                            >
                                {moreLink.title}
                            </Link>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* О версии */}
            <Box
                component="button"
                type="button"
                onClick={() => setIsInfoOpen(prev => !prev)}
                sx={{
                    mt: 2,
                    pt: '12px !important',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    border: 0,
                    borderTop: '1px solid',
                    borderColor: 'neutral.outlinedBorder',
                    background: 'transparent',
                    color: 'inherit',
                    cursor: 'pointer',
                    textAlign: 'left',
                    p: 0,
                }}
            >

                <Typography
                    level="body-xs"
                    sx={{
                        textTransform: 'uppercase',
                        flex: 1,
                    }}
                >
                    О версии
                </Typography>

                <motion.div
                    animate={{
                        rotate: isInfoOpen ? 180 : 0
                    }}
                    transition={{
                        duration: 0.2
                    }}
                    style={{
                        display: 'flex'
                    }}
                >
                    <CaretDownIcon size={18} />
                </motion.div>

            </Box>

            <AnimatePresence initial={false}>

                {isInfoOpen && (
                    <motion.div
                        initial={{
                            height: 0,
                            opacity: 0,
                            marginTop: 0
                        }}
                        animate={{
                            height: 'auto',
                            opacity: 1,
                            marginTop: 8
                        }}
                        exit={{
                            height: 0,
                            opacity: 0,
                            marginTop: 0
                        }}
                        transition={{
                            duration: 0.25,
                            ease: 'easeInOut'
                        }}
                        style={{
                            width: '100%',
                            overflow: 'hidden'
                        }}
                    >

                        {loading ? (
                            <CircularProgress
                                size="sm"
                                variant="soft"
                            />
                        ) : error ? (

                            <Typography
                                color="danger"
                                level="title-sm"
                            >
                                {error ?? 'Ошибка загрузки'}
                            </Typography>
                        ) : (
                            <Box>
                                {(data?.version || data?.releasedAt) && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            gap: 0.5,
                                            flexWrap: 'wrap',
                                            alignItems: 'center'
                                        }}
                                    >
                                        {data.releasedAt && (
                                            <Chip
                                                color="primary"
                                                variant="outlined"
                                                size="sm"
                                            >
                                                <FormatDate dateString={data.releasedAt} />
                                            </Chip>
                                        )}
                                        {data.version && (
                                            <Chip
                                                color="primary"
                                                variant="outlined"
                                                size="sm"
                                            >
                                                {data.version}
                                            </Chip>
                                        )}
                                    </Box>
                                )}
                                <Typography sx={{ mt: 1 }}>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: data?.description
                                        }}
                                    />
                                </Typography>
                            </Box>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </Sheet>
    )
}
